import _toConsumableArray from "/home/ubuntu/code/addictive-builder-all/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _defineProperty from "/home/ubuntu/code/addictive-builder-all/node_modules/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { uuid4, dateTimestampInSeconds, resolvedSyncPromise, truncate, normalize } from '@sentry/utils';
import { Scope } from '../scope.js';
/**
 * Adds common information to events.
 *
 * The information includes release and environment from `options`,
 * breadcrumbs and context (extra, tags and user) from the scope.
 *
 * Information that is already present in the event is never overwritten. For
 * nested objects, such as the context, keys are merged.
 *
 * Note: This also triggers callbacks for `addGlobalEventProcessor`, but not `beforeSend`.
 *
 * @param event The original event.
 * @param hint May contain additional information about the original exception.
 * @param scope A scope containing event metadata.
 * @returns A new event with more information.
 * @hidden
 */

function prepareEvent(options, event, hint, scope) {
  var _options$normalizeDep = options.normalizeDepth,
      normalizeDepth = _options$normalizeDep === void 0 ? 3 : _options$normalizeDep,
      _options$normalizeMax = options.normalizeMaxBreadth,
      normalizeMaxBreadth = _options$normalizeMax === void 0 ? 1000 : _options$normalizeMax;

  var prepared = _objectSpread(_objectSpread({}, event), {}, {
    event_id: event.event_id || hint.event_id || uuid4(),
    timestamp: event.timestamp || dateTimestampInSeconds()
  });

  applyClientOptions(prepared, options);
  applyIntegrationsMetadata(prepared, options.integrations.map(function (i) {
    return i.name;
  })); // If we have scope given to us, use it as the base for further modifications.
  // This allows us to prevent unnecessary copying of data if `captureContext` is not provided.

  var finalScope = scope;

  if (hint.captureContext) {
    finalScope = Scope.clone(finalScope).update(hint.captureContext);
  } // We prepare the result here with a resolved Event.


  var result = resolvedSyncPromise(prepared); // This should be the last thing called, since we want that
  // {@link Hub.addEventProcessor} gets the finished prepared event.
  //
  // We need to check for the existence of `finalScope.getAttachments`
  // because `getAttachments` can be undefined if users are using an older version
  // of `@sentry/core` that does not have the `getAttachments` method.
  // See: https://github.com/getsentry/sentry-javascript/issues/5229

  if (finalScope) {
    // Collect attachments from the hint and scope
    if (finalScope.getAttachments) {
      var attachments = [].concat(_toConsumableArray(hint.attachments || []), _toConsumableArray(finalScope.getAttachments()));

      if (attachments.length) {
        hint.attachments = attachments;
      }
    } // In case we have a hub we reassign it.


    result = finalScope.applyToEvent(prepared, hint);
  }

  return result.then(function (evt) {
    if (typeof normalizeDepth === 'number' && normalizeDepth > 0) {
      return normalizeEvent(evt, normalizeDepth, normalizeMaxBreadth);
    }

    return evt;
  });
}
/**
 *  Enhances event using the client configuration.
 *  It takes care of all "static" values like environment, release and `dist`,
 *  as well as truncating overly long values.
 * @param event event instance to be enhanced
 */


function applyClientOptions(event, options) {
  var environment = options.environment,
      release = options.release,
      dist = options.dist,
      _options$maxValueLeng = options.maxValueLength,
      maxValueLength = _options$maxValueLeng === void 0 ? 250 : _options$maxValueLeng;

  if (!('environment' in event)) {
    event.environment = 'environment' in options ? environment : 'production';
  }

  if (event.release === undefined && release !== undefined) {
    event.release = release;
  }

  if (event.dist === undefined && dist !== undefined) {
    event.dist = dist;
  }

  if (event.message) {
    event.message = truncate(event.message, maxValueLength);
  }

  var exception = event.exception && event.exception.values && event.exception.values[0];

  if (exception && exception.value) {
    exception.value = truncate(exception.value, maxValueLength);
  }

  var request = event.request;

  if (request && request.url) {
    request.url = truncate(request.url, maxValueLength);
  }
}
/**
 * This function adds all used integrations to the SDK info in the event.
 * @param event The event that will be filled with all integrations.
 */


function applyIntegrationsMetadata(event, integrationNames) {
  if (integrationNames.length > 0) {
    event.sdk = event.sdk || {};
    event.sdk.integrations = [].concat(_toConsumableArray(event.sdk.integrations || []), _toConsumableArray(integrationNames));
  }
}
/**
 * Applies `normalize` function on necessary `Event` attributes to make them safe for serialization.
 * Normalized keys:
 * - `breadcrumbs.data`
 * - `user`
 * - `contexts`
 * - `extra`
 * @param event Event
 * @returns Normalized event
 */


function normalizeEvent(event, depth, maxBreadth) {
  if (!event) {
    return null;
  }

  var normalized = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, event), event.breadcrumbs && {
    breadcrumbs: event.breadcrumbs.map(function (b) {
      return _objectSpread(_objectSpread({}, b), b.data && {
        data: normalize(b.data, depth, maxBreadth)
      });
    })
  }), event.user && {
    user: normalize(event.user, depth, maxBreadth)
  }), event.contexts && {
    contexts: normalize(event.contexts, depth, maxBreadth)
  }), event.extra && {
    extra: normalize(event.extra, depth, maxBreadth)
  }); // event.contexts.trace stores information about a Transaction. Similarly,
  // event.spans[] stores information about child Spans. Given that a
  // Transaction is conceptually a Span, normalization should apply to both
  // Transactions and Spans consistently.
  // For now the decision is to skip normalization of Transactions and Spans,
  // so this block overwrites the normalized event to add back the original
  // Transaction information prior to normalization.


  if (event.contexts && event.contexts.trace && normalized.contexts) {
    normalized.contexts.trace = event.contexts.trace; // event.contexts.trace.data may contain circular/dangerous data so we need to normalize it

    if (event.contexts.trace.data) {
      normalized.contexts.trace.data = normalize(event.contexts.trace.data, depth, maxBreadth);
    }
  } // event.spans[].data may contain circular/dangerous data so we need to normalize it


  if (event.spans) {
    normalized.spans = event.spans.map(function (span) {
      // We cannot use the spread operator here because `toJSON` on `span` is non-enumerable
      if (span.data) {
        span.data = normalize(span.data, depth, maxBreadth);
      }

      return span;
    });
  }

  return normalized;
}

export { prepareEvent };